/**
 * Market Forecast Reconciliation - Governed Baseline Publishing
 *
 * Joyson's question this screen answers:
 *   "Who controls whether an uploaded IHS/S&P file becomes the shared baseline,
 *    and can we see the numeric impact before we accept it?"
 *
 * The screen is framed as governed, workspace-wide baseline publishing - not a
 * private "apply all" action by a single user:
 *   1. Upload context: the file is compared to the current baseline.
 *   2. Review buckets: added, removed, and modified vehicle carlines.
 *   3. Impact preview: market-volume, planned-volume, and revenue deltas before commit.
 *   4. Row + bulk decisions: any reviewer can propose decisions.
 *   5. Publish Shared Baseline: only a workspace manager/admin can publish; lower
 *      roles see a review-only state.
 *   6. After publish, the accepted catalog updates for everyone.
 *
 * Endpoints (see docs/packet-1-ihs-governance-impact-contract.md):
 *   GET   /api/v1/ci/reconciliation/sessions
 *   GET   /api/v1/ci/reconciliation/sessions/{id}
 *   GET   /api/v1/ci/reconciliation/sessions/{id}/items
 *   PATCH /api/v1/ci/reconciliation/sessions/{id}/items/{itemId}
 *   POST  /api/v1/ci/reconciliation/sessions/{id}/bulk-decision
 *   GET   /api/v1/ci/reconciliation/sessions/{id}/impact-preview
 *   POST  /api/v1/ci/reconciliation/sessions/{id}/apply   (manager/admin only)
 */
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../../api/client';
import { useAuthStore } from '../../../stores/authStore';
import {
  Upload, FileSpreadsheet, CheckCircle2, AlertTriangle,
  ArrowRight, Clock, RefreshCw, Search, Filter,
  Archive, SkipForward, Check, Loader2, AlertCircle, X, Eye, EyeOff,
  Bookmark, Target, MapPin, Factory, Undo2, Info, ListChecks,
  ShieldCheck, Lock, BarChart3, ChevronRight, Package, Plus
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Progress } from '../../ui/progress';
import { Input } from '../../ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../ui/dialog';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../../ui/sheet';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../../ui/tooltip';
import { Alert, AlertDescription, AlertTitle } from '../../ui/alert';
import { ScrollArea } from '../../ui/scroll-area';
import { cn } from '../../../lib/utils';

// ============================================================================
// FORMAT HELPERS
// ============================================================================

const formatSigned = (n) => {
  const v = Math.round(Number(n) || 0);
  if (v === 0) return '0';
  return `${v > 0 ? '+' : '-'}${Math.abs(v).toLocaleString('en-US')}`;
};

const formatRevenueFromCents = (cents) => {
  const dollars = (Number(cents) || 0) / 100;
  if (dollars === 0) return '$0';
  const abs = Math.abs(dollars);
  const sign = dollars > 0 ? '+' : '-';
  let body;
  if (abs >= 1_000_000) body = `$${(abs / 1_000_000).toFixed(1)}M`;
  else if (abs >= 1_000) body = `$${(abs / 1_000).toFixed(0)}K`;
  else body = `$${abs.toFixed(0)}`;
  return `${sign}${body}`;
};

const formatCompactSigned = (n) => {
  const value = Math.round(Number(n) || 0);
  if (value === 0) return '0';
  const abs = Math.abs(value);
  const sign = value > 0 ? '+' : '-';
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}${(abs / 1_000).toFixed(0)}K`;
  return `${sign}${abs.toLocaleString('en-US')}`;
};

const sumByYear = (obj) =>
  Object.values(obj || {}).reduce((acc, v) => acc + (Number(v) || 0), 0);

const getForecastYears = (totals) => {
  const years = new Set();
  [
    totals?.market_volume_delta_by_year,
    totals?.planned_volume_delta_by_year,
    totals?.revenue_delta_by_year_cents,
  ].forEach((obj) => Object.keys(obj || {}).forEach((year) => years.add(Number(year))));
  const sorted = Array.from(years).filter(Boolean).sort((a, b) => a - b);
  const currentYear = new Date().getFullYear();
  const forwardYears = sorted.filter((year) => year >= currentYear);
  return (forwardYears.length >= 7 ? forwardYears : sorted).slice(0, 7);
};

const formatTakeRate = (row) => {
  if (row?.take_rate_decimal != null) return `${Math.round(Number(row.take_rate_decimal) * 100)}%`;
  if (row?.take_rate == null) return '-';
  const raw = Number(row.take_rate);
  if (!Number.isFinite(raw)) return '-';
  return raw > 1 ? `${Math.round(raw)}%` : `${Math.round(raw * 100)}%`;
};

const PRICE_SOURCE_LABELS = {
  awarded: 'Awarded actual',
  awarded_price: 'Awarded actual',
  quoted: 'Quoted',
  quote: 'Quoted',
  quote_history: 'Quote history',
  price_schedule: 'Price schedule',
  product_group_average: 'Product-group average',
  region_average: 'Regional average',
  manual_override: 'Manual override',
  not_configured: 'Not configured',
};

const priceSourceLabel = (source) => {
  if (!source) return 'Not configured';
  if (PRICE_SOURCE_LABELS[source]) return PRICE_SOURCE_LABELS[source];
  return source.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const distinctPriceSources = (sourcesByYear) => {
  const set = new Set(Object.values(sourcesByYear || {}).filter(Boolean));
  return Array.from(set);
};

// Roles permitted to publish the shared baseline. Mirrors the backend
// require_publish_permission check (workspace admin/manager) and the legacy
// account-role fallback used when workspace roles are disabled.
const WORKSPACE_PUBLISH_ROLES = [
  'admin', 'administrator', 'super admin', 'owner',
  'manager', 'team lead', 'supervisor',
];
const ACCOUNT_PUBLISH_ROLES = ['admin', 'administrator', 'manager', 'super_admin', 'owner'];

// ============================================================================
// HELPER - Decision descriptions
// ============================================================================
const ACTION_DESCRIPTIONS = {
  add_tracking: {
    label: 'Track',
    queuedLabel: 'Track in forecast catalog',
    description: 'Add this carline to the market forecast catalog',
    icon: Bookmark,
  },
  create_opportunity: {
    label: 'Create Opportunity',
    queuedLabel: 'Opportunity will be created',
    description: 'Create a sales opportunity in your pipeline',
    icon: Target,
  },
  skip: {
    label: 'Skip',
    queuedLabel: 'Skip',
    description: 'Do not track this carline',
    icon: SkipForward,
  },
  archive: {
    label: 'Archive',
    queuedLabel: 'Mark inactive',
    description: 'Mark this carline as no longer active in the baseline',
    icon: Archive,
  },
  keep_current: {
    label: 'Keep Current',
    queuedLabel: 'Keep current values',
    description: 'Keep the current baseline values for this carline',
    icon: Check,
  },
  accept_changes: {
    label: 'Accept Update',
    queuedLabel: 'Accept updated values',
    description: 'Accept the new IHS/S&P forecast values',
    icon: Check,
  },
  pending: {
    label: 'Not reviewed',
    queuedLabel: 'Needs review',
    description: 'Select a decision',
    icon: Clock,
  },
};

// ============================================================================
// GOVERNANCE BANNER
// ============================================================================
const GovernanceBanner = ({ onDismiss }) => (
  <Alert className="bg-slate-50 border-slate-200 mb-4">
    <ShieldCheck className="h-4 w-4 text-slate-600" />
    <AlertTitle className="text-slate-900">How a shared baseline is published</AlertTitle>
    <AlertDescription className="text-slate-600">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-2">
        <span className="text-sm"><strong>1. Review</strong> added, removed, and modified vehicle carlines.</span>
        <ArrowRight className="w-4 h-4 text-slate-400 hidden sm:block" />
        <span className="text-sm"><strong>2. Check impact</strong> on market volume and revenue before any commit.</span>
        <ArrowRight className="w-4 h-4 text-slate-400 hidden sm:block" />
        <span className="text-sm"><strong>3. A manager publishes</strong> the shared baseline for the whole workspace.</span>
      </div>
      <p className="text-xs mt-2 text-slate-500">
        Decisions are saved as you review. Nothing changes the shared baseline until a manager or admin publishes.
      </p>
    </AlertDescription>
    <button
      onClick={onDismiss}
      className="absolute top-2 right-2 text-slate-400 hover:text-slate-600"
      aria-label="Dismiss"
    >
      <X className="w-4 h-4" />
    </button>
  </Alert>
);

const BaselineReviewHeader = ({
  session,
  reviewedCount,
  totalCount,
  pendingCount,
  canPublish,
  reviewOnly,
  onPublish,
  isPublishing,
}) => {
  const summary = session?.summary || {};
  const actionableCount = summary.total_requiring_review || 0;
  const uploadedBy = session?.uploaded_by?.name || session?.uploaded_by_name;
  const allReviewed = pendingCount === 0 && totalCount > 0;

  return (
    <Card className="border-slate-200 bg-white shadow-sm">
      <CardContent className="p-5">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700">
                Candidate baseline review
              </Badge>
              <span className="text-xs text-slate-500">
                One published IHS/S&amp;P baseline is shared by the workspace.
              </span>
            </div>

            <div className="mt-4 grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1.35fr)] lg:items-center">
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Current published baseline</p>
                <div className="mt-1 flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-emerald-600" />
                  <p className="font-semibold text-slate-900">Shared workspace baseline</p>
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  Used by Market Forecasts, Pipeline Browse, Program 360, and S&amp;OP market volume.
                </p>
              </div>

              <ArrowRight className="hidden h-5 w-5 text-slate-300 lg:block" />

              <div className="rounded-lg border border-blue-200 bg-blue-50/60 px-4 py-3">
                <p className="text-xs font-medium uppercase tracking-wide text-blue-700">Candidate upload</p>
                <h3 className="mt-1 flex items-center gap-2 truncate text-lg font-semibold text-slate-900">
                  <FileSpreadsheet className="h-4 w-4 shrink-0 text-blue-600" />
                  <span className="truncate">{session.filename}</span>
                </h3>
                <p className="mt-1 text-sm text-slate-600">
                  {(session.file_row_count || 0).toLocaleString()} rows uploaded
                  {' | '}{(summary.programs_unchanged || 0).toLocaleString()} unchanged
                  {' | '}{actionableCount.toLocaleString()} need review
                  {uploadedBy ? ` | uploaded by ${uploadedBy}` : ''}
                </p>
              </div>
            </div>

            <div className="mt-3 grid grid-cols-3 gap-2 text-center sm:max-w-md">
              <div className="rounded-lg bg-emerald-50 px-3 py-2">
                <div className="text-lg font-semibold text-emerald-700">{summary.new_opportunities || 0}</div>
                <div className="text-xs text-emerald-700">Added</div>
              </div>
              <div className="rounded-lg bg-amber-50 px-3 py-2">
                <div className="text-lg font-semibold text-amber-700">{summary.programs_removed || 0}</div>
                <div className="text-xs text-amber-700">Removed</div>
              </div>
              <div className="rounded-lg bg-blue-50 px-3 py-2">
                <div className="text-lg font-semibold text-blue-700">{summary.programs_updated || 0}</div>
                <div className="text-xs text-blue-700">Modified</div>
              </div>
            </div>
          </div>

          <div className="w-full rounded-lg border border-slate-200 bg-white p-4 xl:w-80">
            <div className="mb-2 flex items-center justify-between">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Publish readiness</p>
                <p className="text-lg font-bold text-slate-900">{reviewedCount}/{totalCount} decisions</p>
              </div>
              {allReviewed ? (
                <Badge className="bg-emerald-100 text-emerald-700">Ready</Badge>
              ) : (
                <Badge variant="outline" className="text-slate-600">{pendingCount} pending</Badge>
              )}
            </div>
            <Progress value={totalCount > 0 ? (reviewedCount / totalCount) * 100 : 0} className="mb-3 h-2" />
            <p className="mb-3 text-xs text-slate-500">
              {pendingCount > 0
                ? `${pendingCount} carline${pendingCount === 1 ? '' : 's'} still need a decision before publish.`
                : 'All candidate changes have a decision and are ready for manager publish.'}
            </p>

            {reviewOnly ? (
              <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
                <Lock className="h-4 w-4" />
                <span>Review-only. A manager or admin publishes the shared baseline.</span>
              </div>
            ) : (
              <Button
                onClick={onPublish}
                disabled={!canPublish || !allReviewed || isPublishing}
                className={cn(
                  'w-full',
                  allReviewed && canPublish
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'bg-slate-300 text-slate-500'
                )}
              >
                {isPublishing ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Publishing...</>
                ) : (
                  <><ShieldCheck className="mr-2 h-4 w-4" /> Publish Shared Baseline</>
                )}
              </Button>
            )}
            <p className="mt-2 text-[11px] text-slate-400">Manager / admin only</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ============================================================================
// IMPACT PREVIEW
// ============================================================================
const ImpactCard = ({ label, value, sub, tone }) => {
  const toneMap = {
    market: 'text-slate-900',
    planned: 'text-blue-700',
    revenue: 'text-emerald-700',
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className={cn('text-2xl font-bold mt-1', toneMap[tone])}>{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
};

const ForecastYearImpactTable = ({ totals }) => {
  const years = getForecastYears(totals);
  if (years.length === 0) return null;

  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200">
      <table className="min-w-full divide-y divide-slate-200 text-sm">
        <thead className="bg-slate-50">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              7-year view
            </th>
            {years.map((year) => (
              <th key={year} className="px-3 py-2 text-right text-xs font-semibold text-slate-500">
                {year}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 bg-white">
          <tr>
            <td className="whitespace-nowrap px-3 py-2 font-medium text-slate-700">Market volume</td>
            {years.map((year) => (
              <td key={year} className="whitespace-nowrap px-3 py-2 text-right text-slate-700">
                {formatCompactSigned(totals?.market_volume_delta_by_year?.[year])}
              </td>
            ))}
          </tr>
          <tr>
            <td className="whitespace-nowrap px-3 py-2 font-medium text-slate-700">Planned volume</td>
            {years.map((year) => (
              <td key={year} className="whitespace-nowrap px-3 py-2 text-right text-blue-700">
                {formatCompactSigned(totals?.planned_volume_delta_by_year?.[year])}
              </td>
            ))}
          </tr>
          <tr>
            <td className="whitespace-nowrap px-3 py-2 font-medium text-slate-700">Revenue</td>
            {years.map((year) => (
              <td key={year} className="whitespace-nowrap px-3 py-2 text-right text-slate-900">
                {formatRevenueFromCents(totals?.revenue_delta_by_year_cents?.[year])}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const ImpactSummaryList = ({ title, rows, emptyText }) => (
  <div className="rounded-lg border border-slate-200 bg-white">
    <div className="border-b border-slate-100 px-3 py-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</p>
    </div>
    <div className="divide-y divide-slate-100">
      {rows.length === 0 ? (
        <p className="px-3 py-3 text-sm text-slate-500">{emptyText}</p>
      ) : (
        rows.map((row) => (
          <div key={row.key} className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-3 px-3 py-2 text-sm">
            <div className="min-w-0">
              <p className="truncate font-medium text-slate-900">{row.label}</p>
              <p className="text-xs text-slate-500">{row.count} carline{row.count === 1 ? '' : 's'}</p>
            </div>
            <div className="text-right">
              <p className="font-medium text-slate-700">{formatCompactSigned(row.volumeDelta)}</p>
              <p className="text-xs text-slate-400">market units</p>
            </div>
            <div className="text-right">
              <p className={cn('font-medium', row.revenueCents >= 0 ? 'text-emerald-700' : 'text-red-600')}>
                {formatRevenueFromCents(row.revenueCents)}
              </p>
              <p className="text-xs text-slate-400">revenue</p>
            </div>
          </div>
        ))
      )}
    </div>
  </div>
);

const ImpactPreviewPanel = ({ impact, isLoading, isError, onViewDetail }) => {
  if (isLoading) {
    return (
      <Card className="border-slate-200">
        <CardContent className="p-4 flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Calculating impact preview...
        </CardContent>
      </Card>
    );
  }

  if (isError || !impact) {
    return (
      <Card className="border-slate-200">
        <CardContent className="p-4 text-sm text-slate-500">
          Impact preview is unavailable for this session.
        </CardContent>
      </Card>
    );
  }

  const totals = impact.totals || {};
  const affectedCount = impact.affected_program_parts?.length || 0;
  const revenueByProgramPartId = {};
  (impact.affected_program_parts || []).forEach((part) => {
    revenueByProgramPartId[part.program_part_id] = Number(part.revenue_delta_cents) || 0;
  });
  const rollup = (keyFn, labelFn) => {
    const grouped = {};
    (impact.carline_impacts || []).forEach((carline) => {
      const key = keyFn(carline) || 'Unspecified';
      const ids = carline.affected_program_part_ids || [];
      const revenueCents = ids.reduce((acc, id) => acc + (revenueByProgramPartId[id] || 0), 0);
      if (!grouped[key]) {
        grouped[key] = {
          key,
          label: labelFn(carline) || key,
          count: 0,
          volumeDelta: 0,
          revenueCents: 0,
        };
      }
      grouped[key].count += 1;
      grouped[key].volumeDelta += Number(carline.total_volume_delta) || 0;
      grouped[key].revenueCents += revenueCents;
    });
    return Object.values(grouped)
      .sort((a, b) => Math.abs(b.revenueCents) - Math.abs(a.revenueCents))
      .slice(0, 5);
  };
  const changeTypeLabels = { new: 'Added carlines', removed: 'Removed carlines', modified: 'Modified carlines' };
  const byChangeType = rollup((row) => row.change_type, (row) => changeTypeLabels[row.change_type] || row.change_type);
  const byRegion = rollup((row) => row.display_region, (row) => row.display_region);
  const byOem = rollup((row) => row.display_oem, (row) => row.display_oem);

  return (
    <Card className="border-slate-200 shadow-sm">
      <CardHeader className="py-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <BarChart3 className="h-4 w-4 text-slate-500" />
              7-year impact before publish
            </CardTitle>
            <CardDescription>
              Preview of how the candidate baseline changes linked planning volumes and revenue.
            </CardDescription>
          </div>
          <Badge variant="outline" className="text-[11px] text-slate-500">Preview only, not yet published</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 pt-0">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <ImpactCard
            label="Market volume"
            value={formatSigned(totals.total_market_volume_delta)}
            sub="units across changed carlines"
            tone="market"
          />
          <ImpactCard
            label="Planned volume"
            value={formatSigned(totals.total_planned_volume_delta)}
            sub="units on linked program-parts"
            tone="planned"
          />
          <ImpactCard
            label="Revenue impact"
            value={formatRevenueFromCents(totals.total_revenue_delta_cents)}
            sub="from existing commercial assumptions"
            tone="revenue"
          />
        </div>

        <ForecastYearImpactTable totals={totals} />

        <div className="grid grid-cols-1 gap-3 xl:grid-cols-3">
          <ImpactSummaryList
            title="Impact by change type"
            rows={byChangeType}
            emptyText="No changed carlines in this candidate baseline."
          />
          <ImpactSummaryList
            title="Impact by region"
            rows={byRegion}
            emptyText="No regional impact available."
          />
          <ImpactSummaryList
            title="Impact by OEM"
            rows={byOem}
            emptyText="No OEM impact available."
          />
        </div>

        <div className="flex items-center justify-between">
          <p className="text-xs text-slate-500">
            {affectedCount > 0
              ? `${affectedCount} linked program-part${affectedCount === 1 ? '' : 's'} affected`
              : 'No linked program-parts affected yet'}
          </p>
          <Button variant="outline" size="sm" onClick={onViewDetail} disabled={affectedCount === 0}>
            View impact detail
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const ImpactDetailSheet = ({ open, onOpenChange, impact }) => {
  const affected = impact?.affected_program_parts || [];
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Impact detail</SheetTitle>
          <SheetDescription>
            Preview of how the proposed baseline would affect linked program-parts. Revenue derives from
            existing commercial assumptions and is not published until a manager publishes.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-3">
          {affected.length === 0 ? (
            <div className="text-center py-10 text-slate-500">
              <Package className="w-8 h-8 mx-auto text-slate-300 mb-2" />
              <p className="text-sm">No linked program-parts are affected.</p>
              <p className="text-xs mt-1">Added carlines are not yet linked to a program-part planning context.</p>
            </div>
          ) : (
            affected.map((row) => {
              const plannedDelta = sumByYear(row.planned_volume_delta_by_year);
              const sources = distinctPriceSources(row.price_sources_by_year);
              return (
                <div key={row.program_part_id} className="rounded-lg border border-slate-200 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900 truncate">{row.part_description || row.description || row.part_number}</p>
                      <p className="text-xs text-slate-500">
                        {row.part_number}
                        {row.program_name ? ` | ${row.program_name}` : ''}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className={cn(
                        'text-sm font-semibold',
                        (row.revenue_delta_cents || 0) >= 0 ? 'text-emerald-700' : 'text-red-600'
                      )}>
                        {formatRevenueFromCents(row.revenue_delta_cents)}
                      </p>
                      <p className="text-xs text-slate-500">revenue impact</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                    <div>
                      <p className="text-slate-400">Planned volume</p>
                      <p className="font-medium text-slate-700">{formatSigned(plannedDelta)}</p>
                    </div>
                    <div>
                      <p className="text-slate-400">Take rate</p>
                      <p className="font-medium text-slate-700">
                        {formatTakeRate(row)}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-400">Content / vehicle</p>
                      <p className="font-medium text-slate-700">{row.content_per_vehicle ?? '-'}</p>
                    </div>
                  </div>
                  {sources.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1.5 mt-2">
                      <span className="text-xs text-slate-400">Price source:</span>
                      {sources.map((s) => (
                        <Badge key={s} variant="outline" className="text-[10px] text-slate-600">
                          {priceSourceLabel(s)}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

// ============================================================================
// BULK ACTIONS
// ============================================================================
const BULK_DECISIONS = [
  { key: 'accept_added', label: 'Accept all added', typeKey: 'new' },
  { key: 'accept_modified', label: 'Accept all modified', typeKey: 'modified' },
  { key: 'accept_removed', label: 'Accept all removed', typeKey: 'removed' },
  { key: 'reject_all', label: 'Keep current for all', typeKey: 'all' },
];

const BulkActionsBar = ({ counts, onBulk, isPending }) => {
  const visible = BULK_DECISIONS.filter((d) =>
    d.typeKey === 'all' ? (counts.total || 0) > 0 : (counts[d.typeKey] || 0) > 0
  );
  if (visible.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs font-medium text-slate-500 flex items-center gap-1">
        <ListChecks className="w-3.5 h-3.5" /> Bulk decisions:
      </span>
      {visible.map((d) => (
        <Button
          key={d.key}
          variant="outline"
          size="sm"
          disabled={isPending}
          onClick={() => onBulk(d)}
          className="h-8 text-xs"
        >
          {d.label}
        </Button>
      ))}
    </div>
  );
};

// ============================================================================
// STAT CARD (bucket selector)
// ============================================================================
const StatCard = ({ title, count, icon: Icon, color, isActive, onClick }) => {
  const colorMap = {
    emerald: { bg: 'bg-emerald-50', icon: 'text-emerald-600', border: 'border-emerald-200', ring: 'ring-emerald-500' },
    amber: { bg: 'bg-amber-50', icon: 'text-amber-600', border: 'border-amber-200', ring: 'ring-amber-500' },
    blue: { bg: 'bg-blue-50', icon: 'text-blue-600', border: 'border-blue-200', ring: 'ring-blue-500' },
  };
  const colors = colorMap[color];

  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center gap-3 p-4 rounded-xl border transition-all text-left w-full',
        colors.bg, colors.border,
        isActive ? `ring-2 ${colors.ring}` : 'hover:shadow-sm'
      )}
    >
      <Icon className={cn('w-5 h-5', colors.icon)} />
      <div>
        <span className="text-2xl font-bold text-slate-900">{count}</span>
        <p className="text-xs text-slate-600">{title}</p>
      </div>
    </button>
  );
};

// ============================================================================
// RECONCILIATION ITEM ROW
// ============================================================================
const ReconciliationItemRow = ({ item, onActionChange, impact }) => {
  const [showChanges, setShowChanges] = useState(false);
  const currentAction = item.user_action || 'pending';
  const isReviewed = currentAction !== 'pending';
  const actionInfo = ACTION_DESCRIPTIONS[currentAction];

  const severityIndicator = {
    high: 'bg-red-500',
    medium: 'bg-amber-500',
    low: 'bg-emerald-500',
  };

  const typeConfig = {
    new: { badge: 'bg-emerald-100 text-emerald-700', label: 'ADDED', actions: ['add_tracking', 'create_opportunity', 'skip'] },
    removed: { badge: 'bg-red-100 text-red-700', label: 'REMOVED', actions: ['archive', 'keep_current'] },
    modified: { badge: 'bg-blue-100 text-blue-700', label: 'MODIFIED', actions: ['accept_changes', 'keep_current'] },
  };

  const config = typeConfig[item.change_type];
  const primaryChange = item.changes?.find((c) => c.is_significant) || item.changes?.[0];

  const handleActionClick = (action) => {
    if (currentAction === action) {
      onActionChange(item.id, 'pending');
    } else {
      onActionChange(item.id, action);
    }
  };

  return (
    <div
      className={cn(
        'border rounded-lg transition-all bg-white',
        isReviewed && 'border-l-4',
        currentAction === 'create_opportunity' && 'border-l-emerald-500 bg-emerald-50/30',
        currentAction === 'add_tracking' && 'border-l-blue-500 bg-blue-50/30',
        currentAction === 'accept_changes' && 'border-l-emerald-500 bg-emerald-50/30',
        currentAction === 'archive' && 'border-l-amber-500 bg-amber-50/30',
        currentAction === 'skip' && 'border-l-slate-400 bg-slate-50/50',
        currentAction === 'keep_current' && 'border-l-slate-400 bg-slate-50/50',
      )}
    >
      <div className="flex items-center gap-3 px-4 py-3">
        <div className={cn('w-1 h-10 rounded-full flex-shrink-0', severityIndicator[item.severity])} />

        <Badge className={cn('text-[10px] px-1.5 py-0.5 font-semibold', config.badge)}>
          {config.label}
        </Badge>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-slate-900 truncate">
              {item.display_name || 'Unknown carline'}
            </span>
            {item.has_linked_opportunities && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                  </TooltipTrigger>
                  <TooltipContent><p>Linked to an existing program-part / opportunity</p></TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <span>{item.display_oem}</span>
            {item.display_region && (
              <>
                <span className="text-slate-300">|</span>
                <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{item.display_region}</span>
              </>
            )}
            {item.display_volume > 0 && (
              <>
                <span className="text-slate-300">|</span>
                <span className="flex items-center gap-0.5">
                  <Factory className="w-3 h-3" />{(item.display_volume / 1000).toFixed(0)}K/yr
                </span>
              </>
            )}
          </div>
        </div>

        {/* Impact badge from dry-run preview */}
        {impact && (
          <div className="hidden lg:flex flex-col items-end text-right">
            {impact.linkedCount > 0 ? (
              <>
                <span className={cn(
                  'text-sm font-semibold',
                  (impact.revenueCents || 0) >= 0 ? 'text-emerald-700' : 'text-red-600'
                )}>
                  {formatRevenueFromCents(impact.revenueCents)}
                </span>
                <span className="text-[11px] text-slate-400">
                  {impact.linkedCount} linked program-part{impact.linkedCount === 1 ? '' : 's'}
                </span>
              </>
            ) : (
              <span className="text-[11px] text-slate-400">Not yet linked to a planning context</span>
            )}
          </div>
        )}

        {/* Modified value comparison */}
        {item.change_type === 'modified' && primaryChange && item.changes?.length > 0 && (
          <button
            onClick={() => setShowChanges(!showChanges)}
            className="hidden md:inline text-xs text-blue-600 hover:text-blue-700"
          >
            {showChanges ? 'Hide changes' : 'View changes'}
          </button>
        )}

        {isReviewed && (
          <Badge
            variant="outline"
            className={cn(
              'text-xs whitespace-nowrap',
              currentAction === 'create_opportunity' && 'border-emerald-300 text-emerald-700 bg-emerald-50',
              currentAction === 'add_tracking' && 'border-blue-300 text-blue-700 bg-blue-50',
              currentAction === 'accept_changes' && 'border-emerald-300 text-emerald-700 bg-emerald-50',
              currentAction === 'archive' && 'border-amber-300 text-amber-700 bg-amber-50',
              (currentAction === 'skip' || currentAction === 'keep_current') && 'border-slate-300 text-slate-600 bg-slate-50',
            )}
          >
            {actionInfo.queuedLabel}
          </Badge>
        )}

        <div className="flex items-center gap-1">
          {config.actions.map((actionKey) => {
            const action = ACTION_DESCRIPTIONS[actionKey];
            const ActionIcon = action.icon;
            const isSelected = currentAction === actionKey;
            return (
              <TooltipProvider key={actionKey}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => handleActionClick(actionKey)}
                      className={cn(
                        'inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all duration-150 active:scale-95',
                        isSelected
                          ? 'bg-blue-600 text-white shadow-sm ring-2 ring-blue-300 ring-offset-1'
                          : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
                      )}
                    >
                      <ActionIcon className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">{action.label}</span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p className="font-medium">{action.label}</p>
                    <p className="text-xs text-slate-400">{action.description}</p>
                    {isSelected && <p className="text-xs text-blue-400 mt-1">Click again to clear</p>}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          })}

          {isReviewed && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => onActionChange(item.id, 'pending')}
                    className="ml-1 p-1.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all"
                  >
                    <Undo2 className="w-3.5 h-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent><p>Clear decision</p></TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>

      {showChanges && item.changes && (
        <div className="px-4 pb-3 pt-0">
          <div className="ml-6 space-y-1 text-sm">
            {item.changes.map((change, idx) => (
              <div key={idx} className="flex items-center gap-3 py-1 text-slate-600">
                <span className="w-32 truncate">{change.label}:</span>
                <span className="text-slate-400 line-through">
                  {typeof change.old_value === 'number' ? change.old_value.toLocaleString() : change.old_value}
                </span>
                <ArrowRight className="w-3 h-3 text-slate-400" />
                <span className="font-medium text-slate-900">
                  {typeof change.new_value === 'number' ? change.new_value.toLocaleString() : change.new_value}
                </span>
                {change.pct_change !== undefined && (
                  <Badge variant="outline" className={cn(
                    'text-[10px]',
                    change.pct_change > 0 ? 'text-emerald-600 border-emerald-200' : 'text-red-600 border-red-200'
                  )}>
                    {change.pct_change > 0 ? '+' : ''}{change.pct_change}%
                  </Badge>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// UPLOAD ZONE
// ============================================================================
const UploadZone = ({ onUpload, isUploading }) => {
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = React.useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) onUpload(e.dataTransfer.files[0]);
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) onUpload(e.target.files[0]);
  };

  return (
    <div
      className={cn(
        'relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200 cursor-pointer',
        dragActive ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50',
        isUploading && 'pointer-events-none opacity-60'
      )}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      onClick={() => fileInputRef.current?.click()}
    >
      <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleFileSelect} className="hidden" />
      {isUploading ? (
        <div className="space-y-3">
          <Loader2 className="w-10 h-10 mx-auto text-blue-500 animate-spin" />
          <p className="text-base font-medium text-slate-700">Processing your file...</p>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="w-14 h-14 mx-auto bg-slate-100 rounded-xl flex items-center justify-center">
            <FileSpreadsheet className="w-7 h-7 text-slate-500" />
          </div>
          <div>
            <p className="text-base font-medium text-slate-700">
              Drop the latest IHS/S&amp;P Mobility forecast file here or click to browse
            </p>
            <p className="text-sm text-slate-400 mt-1">Supports .xlsx and .xls files</p>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// PROCESSING STATE
// ============================================================================
const ProcessingState = ({ session }) => (
  <Card className="border-slate-200 bg-slate-50/50">
    <CardContent className="p-8 text-center">
      <div className="w-16 h-16 mx-auto bg-slate-100 rounded-full flex items-center justify-center mb-4">
        <Loader2 className="w-8 h-8 text-slate-500 animate-spin" />
      </div>
      <h3 className="text-lg font-semibold text-slate-900 mb-2">Comparing against the current baseline</h3>
      <p className="text-slate-600 mb-4">
        Checking {session?.file_row_count?.toLocaleString() || '...'} uploaded rows for added, removed, and modified carlines
      </p>
      <Progress value={session?.progress_percent || 0} className="h-2 max-w-md mx-auto" />
      <p className="text-sm text-slate-500 mt-2">{session?.progress_percent || 0}% complete</p>
    </CardContent>
  </Card>
);

// ============================================================================
// PUBLISHED STATE
// ============================================================================
const PublishedState = ({ results, onReset }) => (
  <Card className="border-emerald-200 bg-emerald-50/40">
    <CardContent className="p-8 text-center">
      <div className="w-16 h-16 mx-auto bg-emerald-100 rounded-full flex items-center justify-center mb-4">
        <CheckCircle2 className="w-8 h-8 text-emerald-600" />
      </div>
      <h3 className="text-xl font-bold text-slate-900 mb-2">Shared baseline published</h3>
      <p className="text-slate-600 mb-6">
        The accepted catalog is updated for everyone. Market Forecasts and Browse Market Forecasts now read this baseline.
      </p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto mb-6">
        <div className="p-4 bg-white rounded-lg border border-slate-200">
          <p className="text-3xl font-bold text-emerald-600">{results?.records_created || 0}</p>
          <p className="text-sm text-slate-500">Carlines added</p>
        </div>
        <div className="p-4 bg-white rounded-lg border border-slate-200">
          <p className="text-3xl font-bold text-blue-600">{results?.records_updated || 0}</p>
          <p className="text-sm text-slate-500">Carlines updated</p>
        </div>
        <div className="p-4 bg-white rounded-lg border border-slate-200">
          <p className="text-3xl font-bold text-amber-600">{results?.records_archived || 0}</p>
          <p className="text-sm text-slate-500">Marked inactive</p>
        </div>
        <div className="p-4 bg-white rounded-lg border border-slate-200">
          <p className="text-3xl font-bold text-slate-700">{results?.opportunities_created || 0}</p>
          <p className="text-sm text-slate-500">Opportunities created</p>
        </div>
      </div>
      <Button onClick={onReset} variant="outline">Back to Market Forecasts</Button>
    </CardContent>
  </Card>
);

// ============================================================================
// AUDIT HISTORY PANEL
// ============================================================================
const AuditHistoryPanel = () => {
  const { data: auditData, isLoading, error } = useQuery({
    queryKey: ['reconciliation-audit-log'],
    queryFn: async () => {
      const response = await apiClient.get('/api/v1/ci/reconciliation/audit-log?limit=100');
      return response.data;
    },
    retry: 1,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const actionTypeLabels = {
    record_created: { label: 'Carline added', icon: Plus, color: 'text-emerald-600 bg-emerald-50' },
    record_updated: { label: 'Carline updated', icon: RefreshCw, color: 'text-blue-600 bg-blue-50' },
    record_archived: { label: 'Carline archived', icon: Archive, color: 'text-amber-600 bg-amber-50' },
    opportunity_created: { label: 'Opportunity created', icon: Target, color: 'text-slate-600 bg-slate-50' },
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Loader2 className="w-6 h-6 mx-auto text-slate-400 animate-spin" />
          <p className="text-sm text-slate-500 mt-2">Loading history...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <AlertCircle className="w-10 h-10 mx-auto text-red-400 mb-3" />
          <p className="text-slate-600 font-medium">Failed to load history</p>
          <p className="text-sm text-slate-500 mt-1">{error.message}</p>
        </CardContent>
      </Card>
    );
  }

  const logs = auditData?.logs || [];

  if (logs.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Clock className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-600 font-medium">No history yet</p>
          <p className="text-sm text-slate-500 mt-1">Published baselines will appear here</p>
        </CardContent>
      </Card>
    );
  }

  const groupedBySession = logs.reduce((acc, log) => {
    const key = log.session_id;
    if (!acc[key]) acc[key] = { session_id: log.session_id, filename: log.session_filename, logs: [] };
    acc[key].logs.push(log);
    return acc;
  }, {});

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Clock className="w-4 h-4" /> Publish history
        </CardTitle>
        <CardDescription>Audit trail of published baseline changes</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <ScrollArea className="h-[400px]">
          <div className="divide-y">
            {Object.values(groupedBySession).map((group) => (
              <div key={group.session_id} className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <FileSpreadsheet className="w-4 h-4 text-slate-400" />
                  <span className="font-medium text-slate-900">{group.filename}</span>
                  <Badge variant="outline" className="text-xs">{group.logs.length} actions</Badge>
                </div>
                <div className="space-y-2 ml-6">
                  {group.logs.slice(0, 5).map((log) => {
                    const config = actionTypeLabels[log.action_type] || { label: log.action_type, icon: Info, color: 'text-slate-600 bg-slate-50' };
                    const LogIcon = config.icon;
                    return (
                      <div key={log.id} className="flex items-start gap-3 text-sm">
                        <div className={cn('p-1 rounded', config.color)}>
                          <LogIcon className="w-3 h-3" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-slate-900">
                            <span className="font-medium">{config.label}</span>
                            {log.after_data?.production_nameplate && (
                              <span className="text-slate-500"> - {log.after_data.sales_parent} {log.after_data.production_nameplate}</span>
                            )}
                          </p>
                          <p className="text-xs text-slate-400">{formatDate(log.created_at)} by {log.performed_by_name}</p>
                        </div>
                      </div>
                    );
                  })}
                  {group.logs.length > 5 && (
                    <p className="text-xs text-slate-400 ml-6">+{group.logs.length - 5} more actions</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const IHSReconciliationPage = () => {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const [activeSession, setActiveSession] = useState(null);
  const [activeTab, setActiveTab] = useState('new');
  const [searchQuery, setSearchQuery] = useState('');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [publishResults, setPublishResults] = useState(null);
  const [publishError, setPublishError] = useState(null);
  const [showResolved, setShowResolved] = useState(true);
  const [showInstructions, setShowInstructions] = useState(true);
  const [viewMode, setViewMode] = useState('review');
  const [showImpactDetail, setShowImpactDetail] = useState(false);
  const [pendingBulk, setPendingBulk] = useState(null);

  // --- Workspace role (for publish gating) ---
  const { data: workspaceData, isFetched: roleFetched } = useQuery({
    queryKey: ['current-workspace-role'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/workspaces/current');
      return res.data?.data || null;
    },
    staleTime: 5 * 60 * 1000,
  });

  const canPublish = useMemo(() => {
    const accountRole = (user?.role || '').toLowerCase();
    const accountAllows = ACCOUNT_PUBLISH_ROLES.includes(accountRole);
    const wsRole = (workspaceData?.role?.role_name || '').toLowerCase();
    const perms = workspaceData?.role?.permissions || {};
    const wsAllows =
      WORKSPACE_PUBLISH_ROLES.includes(wsRole) ||
      Boolean(perms?.members?.changeRoles) ||
      Boolean(perms?.settings?.manage);
    return accountAllows || wsAllows;
  }, [user, workspaceData]);

  // Only show the review-only state once we have actually resolved the role.
  const reviewOnly = roleFetched && !canPublish;

  // --- Sessions ---
  const { data: sessionsData, isLoading: sessionsLoading } = useQuery({
    queryKey: ['reconciliation-sessions'],
    queryFn: async () => {
      const response = await apiClient.get('/api/v1/ci/reconciliation/sessions?limit=10');
      return response.data;
    },
    refetchInterval: (data) => {
      const processing = data?.sessions?.find((s) => s.status === 'processing');
      return processing ? 3000 : false;
    },
  });

  // --- Active session detail ---
  const { data: sessionDetails, refetch: refetchSession } = useQuery({
    queryKey: ['reconciliation-session', activeSession],
    queryFn: async () => {
      const response = await apiClient.get(`/api/v1/ci/reconciliation/sessions/${activeSession}`);
      return response.data;
    },
    enabled: !!activeSession,
    refetchInterval: (data) => (data?.status === 'processing' ? 2000 : false),
  });

  const isReadyForReview = sessionDetails?.status === 'ready_for_review';

  // --- All items (for summary / publish gating) ---
  const { data: allItemsData } = useQuery({
    queryKey: ['reconciliation-all-items', activeSession],
    queryFn: async () => {
      const response = await apiClient.get(`/api/v1/ci/reconciliation/sessions/${activeSession}/items?limit=200`);
      return response.data;
    },
    enabled: !!activeSession && isReadyForReview,
    staleTime: 0,
  });

  // --- Filtered items (for display) ---
  const { data: itemsData, isLoading: itemsLoading } = useQuery({
    queryKey: ['reconciliation-items', activeSession, activeTab, searchQuery, severityFilter],
    queryFn: async () => {
      const params = new URLSearchParams({ change_type: activeTab, limit: '100', sort_by: 'severity' });
      if (searchQuery) params.append('search', searchQuery);
      if (severityFilter !== 'all') params.append('severity', severityFilter);
      const response = await apiClient.get(`/api/v1/ci/reconciliation/sessions/${activeSession}/items?${params}`);
      return response.data;
    },
    enabled: !!activeSession && isReadyForReview,
  });

  // --- Impact preview (dry run) ---
  const { data: impactData, isLoading: impactLoading, isError: impactError } = useQuery({
    queryKey: ['reconciliation-impact', activeSession],
    queryFn: async () => {
      const response = await apiClient.get(`/api/v1/ci/reconciliation/sessions/${activeSession}/impact-preview`);
      return response.data;
    },
    enabled: !!activeSession && isReadyForReview,
    staleTime: 0,
  });

  // Per-carline impact map keyed by review item id (for row-level badges)
  const impactByItemId = useMemo(() => {
    if (!impactData) return {};
    const partRevenueById = {};
    (impactData.affected_program_parts || []).forEach((p) => {
      partRevenueById[p.program_part_id] = Number(p.revenue_delta_cents) || 0;
    });
    const map = {};
    (impactData.carline_impacts || []).forEach((c) => {
      const ids = c.affected_program_part_ids || [];
      const revenueCents = ids.reduce((acc, id) => acc + (partRevenueById[id] || 0), 0);
      map[c.item_id] = {
        linkedCount: ids.length,
        revenueCents,
        volumeDelta: c.total_volume_delta || 0,
      };
    });
    return map;
  }, [impactData]);

  // --- Mutations ---
  const uploadMutation = useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.post('/api/v1/ci/reconciliation/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
    onSuccess: (data) => {
      setActiveSession(data.session_id);
      queryClient.invalidateQueries(['reconciliation-sessions']);
    },
  });

  const updateActionMutation = useMutation({
    mutationFn: async ({ itemId, action }) => {
      const response = await apiClient.patch(
        `/api/v1/ci/reconciliation/sessions/${activeSession}/items/${itemId}`,
        { action }
      );
      return response.data;
    },
    onMutate: async ({ itemId, action }) => {
      await queryClient.cancelQueries(['reconciliation-items']);
      await queryClient.cancelQueries(['reconciliation-all-items']);
      const previousItems = queryClient.getQueryData(['reconciliation-items', activeSession, activeTab, searchQuery, severityFilter]);
      const previousAllItems = queryClient.getQueryData(['reconciliation-all-items', activeSession]);
      const updateItems = (oldData) => {
        if (!oldData?.items) return oldData;
        return { ...oldData, items: oldData.items.map((item) => (item.id === itemId ? { ...item, user_action: action } : item)) };
      };
      queryClient.setQueryData(['reconciliation-items', activeSession, activeTab, searchQuery, severityFilter], updateItems);
      queryClient.setQueryData(['reconciliation-all-items', activeSession], updateItems);
      return { previousItems, previousAllItems };
    },
    onError: (err, variables, context) => {
      if (context?.previousItems) {
        queryClient.setQueryData(['reconciliation-items', activeSession, activeTab, searchQuery, severityFilter], context.previousItems);
      }
      if (context?.previousAllItems) {
        queryClient.setQueryData(['reconciliation-all-items', activeSession], context.previousAllItems);
      }
    },
    onSettled: () => {
      setTimeout(() => {
        queryClient.invalidateQueries(['reconciliation-items']);
        queryClient.invalidateQueries(['reconciliation-all-items']);
        refetchSession();
      }, 500);
    },
  });

  const bulkDecisionMutation = useMutation({
    mutationFn: async (decision) => {
      const response = await apiClient.post(
        `/api/v1/ci/reconciliation/sessions/${activeSession}/bulk-decision`,
        { decision }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['reconciliation-items']);
      queryClient.invalidateQueries(['reconciliation-all-items']);
      queryClient.invalidateQueries(['reconciliation-impact']);
      refetchSession();
      setPendingBulk(null);
    },
  });

  const publishMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.post(
        `/api/v1/ci/reconciliation/sessions/${activeSession}/apply`,
        { confirm: true }
      );
      return response.data;
    },
    onSuccess: (data) => {
      setPublishResults(data.results);
      setPublishError(null);
      setShowPublishDialog(false);
      queryClient.invalidateQueries(['reconciliation-sessions']);
      refetchSession();
    },
    onError: (err) => {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 403) {
        setPublishError('Publishing the shared baseline requires a manager or admin role.');
      } else {
        setPublishError(typeof detail === 'string' ? detail : 'Publishing failed. Please try again.');
      }
    },
  });

  // Auto-select most recent reviewable session
  const reviewSessionIdRef = React.useRef(null);
  useEffect(() => {
    if (!sessionsData?.sessions?.length) return;
    if (activeSession) return;
    const reviewSession = sessionsData.sessions.find(
      (s) => s.status === 'ready_for_review' || s.status === 'processing'
    );
    if (reviewSession && reviewSessionIdRef.current !== reviewSession.id) {
      reviewSessionIdRef.current = reviewSession.id;
      setActiveSession(reviewSession.id);
    }
  }, [sessionsData?.sessions, activeSession]);

  const handleActionChange = useCallback((itemId, action) => {
    updateActionMutation.mutate({ itemId, action });
  }, [updateActionMutation]);

  const handleReset = () => {
    setPublishResults(null);
    setPublishError(null);
    setActiveSession(null);
    reviewSessionIdRef.current = null;
  };

  // --- Derived ---
  const items = itemsData?.items || [];
  const filteredItems = useMemo(() => {
    if (showResolved) return items;
    return items.filter((item) => item.user_action === 'pending');
  }, [items, showResolved]);

  const allItems = allItemsData?.items || [];
  const pendingCount = allItems.filter((i) => !i.user_action || i.user_action === 'pending').length;
  const reviewedCount = allItems.length - pendingCount;
  const canPublishNow = pendingCount === 0 && allItems.length > 0 && isReadyForReview;

  const bulkCounts = useMemo(() => ({
    new: allItems.filter((i) => i.change_type === 'new').length,
    modified: allItems.filter((i) => i.change_type === 'modified').length,
    removed: allItems.filter((i) => i.change_type === 'removed').length,
    total: allItems.length,
  }), [allItems]);

  return (
    <div className="space-y-4">
      {/* View Toggle */}
      <div className="flex items-center justify-end">
        <div className="flex bg-slate-100 rounded-lg p-1">
          <button
            onClick={() => setViewMode('review')}
            className={cn('px-3 py-1.5 rounded-md text-sm font-medium transition-all',
              viewMode === 'review' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700')}
          >
            <RefreshCw className="w-4 h-4 inline mr-1.5" /> Review
          </button>
          <button
            onClick={() => setViewMode('history')}
            className={cn('px-3 py-1.5 rounded-md text-sm font-medium transition-all',
              viewMode === 'history' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700')}
          >
            <Clock className="w-4 h-4 inline mr-1.5" /> History
          </button>
        </div>
      </div>

      {viewMode === 'history' && <AuditHistoryPanel />}

      {viewMode === 'review' && (
        <>
          {publishResults && <PublishedState results={publishResults} onReset={handleReset} />}

          {!activeSession && !sessionsLoading && !publishResults && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="w-5 h-5" /> Upload vehicle forecast file
                </CardTitle>
                <CardDescription>
                  Upload the latest IHS/S&amp;P Mobility vehicle forecast file to compare it against the current shared baseline
                </CardDescription>
              </CardHeader>
              <CardContent>
                <UploadZone onUpload={(file) => uploadMutation.mutate(file)} isUploading={uploadMutation.isPending} />
              </CardContent>
            </Card>
          )}

          {sessionDetails?.status === 'processing' && <ProcessingState session={sessionDetails} />}

          {isReadyForReview && !publishResults && (
            <>
              <BaselineReviewHeader
                session={sessionDetails}
                reviewedCount={reviewedCount}
                totalCount={allItems.length}
                pendingCount={pendingCount}
                canPublish={canPublish && canPublishNow}
                reviewOnly={reviewOnly}
                onPublish={() => { setPublishError(null); setShowPublishDialog(true); }}
                isPublishing={publishMutation.isPending}
              />

              {showInstructions && <GovernanceBanner onDismiss={() => setShowInstructions(false)} />}

              <ImpactPreviewPanel
                impact={impactData}
                isLoading={impactLoading}
                isError={impactError}
                onViewDetail={() => setShowImpactDetail(true)}
              />

              {/* Bucket selectors */}
              <div className="grid grid-cols-3 gap-3">
                <StatCard
                  title="Carlines Added" count={sessionDetails.summary?.new_opportunities || 0}
                  icon={Plus} color="emerald" isActive={activeTab === 'new'} onClick={() => setActiveTab('new')}
                />
                <StatCard
                  title="Carlines Removed" count={sessionDetails.summary?.programs_removed || 0}
                  icon={Archive} color="amber" isActive={activeTab === 'removed'} onClick={() => setActiveTab('removed')}
                />
                <StatCard
                  title="Carlines Modified" count={sessionDetails.summary?.programs_updated || 0}
                  icon={RefreshCw} color="blue" isActive={activeTab === 'modified'} onClick={() => setActiveTab('modified')}
                />
              </div>

              {/* Bulk actions */}
              <BulkActionsBar
                counts={bulkCounts}
                isPending={bulkDecisionMutation.isPending}
                onBulk={(d) => setPendingBulk(d)}
              />

              {/* Filters */}
              <div className="flex items-center gap-3">
                <div className="relative flex-1 max-w-xs">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search carlines..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 h-9"
                  />
                </div>
                <Select value={severityFilter} onValueChange={setSeverityFilter}>
                  <SelectTrigger className="w-[130px] h-9">
                    <Filter className="w-4 h-4 mr-1" />
                    <SelectValue placeholder="Severity" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All severity</SelectItem>
                    <SelectItem value="high">High only</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="ghost" size="sm" onClick={() => setShowResolved(!showResolved)} className="h-9">
                  {showResolved ? (<><Eye className="w-4 h-4 mr-1" /> Show all</>) : (<><EyeOff className="w-4 h-4 mr-1" /> Pending only</>)}
                </Button>
              </div>

              {/* Items list */}
              <Card>
                <CardHeader className="py-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">
                      {activeTab === 'new' && 'Added vehicle carlines'}
                      {activeTab === 'removed' && 'Removed vehicle carlines'}
                      {activeTab === 'modified' && 'Modified vehicle carlines'}
                    </CardTitle>
                    <Badge variant="outline">{filteredItems.length} items</Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  {itemsLoading ? (
                    <div className="py-12 text-center"><Loader2 className="w-6 h-6 mx-auto text-slate-400 animate-spin" /></div>
                  ) : filteredItems.length === 0 ? (
                    <div className="py-12 text-center">
                      <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500" />
                      <p className="text-slate-600 mt-2 font-medium">Nothing to review here</p>
                      <p className="text-slate-500 text-sm">No carlines in this bucket</p>
                    </div>
                  ) : (
                    <ScrollArea className="h-[400px]">
                      <div className="space-y-2 p-4">
                        {filteredItems.map((item) => (
                          <ReconciliationItemRow
                            key={item.id}
                            item={item}
                            onActionChange={handleActionChange}
                            impact={impactByItemId[item.id]}
                          />
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </>
      )}

      {/* Impact detail drawer */}
      <ImpactDetailSheet open={showImpactDetail} onOpenChange={setShowImpactDetail} impact={impactData} />

      {/* Bulk decision confirmation */}
      <Dialog open={!!pendingBulk} onOpenChange={(open) => !open && setPendingBulk(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{pendingBulk?.label}?</DialogTitle>
            <DialogDescription>
              This sets a decision for every carline in this bucket. You can still change individual carlines
              before publishing. Nothing is published yet.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingBulk(null)}>Cancel</Button>
            <Button
              onClick={() => bulkDecisionMutation.mutate(pendingBulk.key)}
              disabled={bulkDecisionMutation.isPending}
            >
              {bulkDecisionMutation.isPending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Applying...</>
              ) : (
                <><Check className="w-4 h-4 mr-2" /> {pendingBulk?.label}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Publish confirmation */}
      <Dialog open={showPublishDialog} onOpenChange={setShowPublishDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-blue-600" /> Publish shared baseline?
            </DialogTitle>
            <DialogDescription>
              Publishing updates the shared market baseline for everyone in this workspace.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600 space-y-2">
              <p className="flex items-start gap-2">
                <Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                Linked program-part planning recalculates market volume from the new baseline.
              </p>
              <p className="flex items-start gap-2">
                <Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                Sales-owned assumptions are not changed: take rate, allocation, content per vehicle, price, and LTA.
              </p>
            </div>
            {impactData?.totals && (
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-md bg-slate-50 px-2 py-2">
                  <p className="text-sm font-semibold text-slate-900">{formatSigned(impactData.totals.total_market_volume_delta)}</p>
                  <p className="text-[11px] text-slate-500">market units</p>
                </div>
                <div className="rounded-md bg-slate-50 px-2 py-2">
                  <p className="text-sm font-semibold text-blue-700">{formatSigned(impactData.totals.total_planned_volume_delta)}</p>
                  <p className="text-[11px] text-slate-500">planned units</p>
                </div>
                <div className="rounded-md bg-slate-50 px-2 py-2">
                  <p className="text-sm font-semibold text-emerald-700">{formatRevenueFromCents(impactData.totals.total_revenue_delta_cents)}</p>
                  <p className="text-[11px] text-slate-500">revenue</p>
                </div>
              </div>
            )}
            {publishError && (
              <Alert className="bg-red-50 border-red-200">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-700 text-sm">{publishError}</AlertDescription>
              </Alert>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPublishDialog(false)}>Cancel</Button>
            <Button
              onClick={() => publishMutation.mutate()}
              disabled={publishMutation.isPending}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {publishMutation.isPending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Publishing...</>
              ) : (
                <><ShieldCheck className="w-4 h-4 mr-2" /> Publish Shared Baseline</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IHSReconciliationPage;
